import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, GlobalStyles } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplayIcon from "@mui/icons-material/Replay";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { fetchLatestTestSubmission } from "../api/backend";
import { useTenant } from "../contexts/TenantContext";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  ink:      "var(--color-ink)",
  ink2:     "var(--color-ink-2)",
  ink3:     "var(--color-ink-3)",
  ink4:     "var(--color-ink-4)",
  line:     "var(--color-hairline-cloud)",
  line2:    "#E5E7EB",
  surface:  "#FFFFFF",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-2)",
  pageBg:   "var(--color-surface-2)",
  primary:  "var(--color-primary)",
  p700:     "#182E6E",
  p50:      "var(--color-primary-10)",
  green:    "#15803D",
  green50:  "rgba(21,128,61,.10)",
  green100: "rgba(21,128,61,.18)",
  amber:    "#B5651D",
  amber50:  "rgba(181,101,29,.10)",
  danger:   "#B42318",
  danger50: "rgba(180,35,24,.08)",
  danger100:"rgba(180,35,24,.18)",
  blue:     "var(--color-primary)",
  shadow:   "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
  mono:     "'JetBrains Mono', 'Courier New', monospace",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatTimeSec(sec) {
  if (!sec || sec <= 0) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function getTypeLabel(type) {
  switch (type) {
    case "MULTIPLE_CHOICE": return "Multiple choice";
    case "TRUE_FALSE":      return "True / False";
    case "MULTIPLE_SELECT": return "Select all that apply";
    case "MATCHING":        return "Matching";
    case "SHORT_ANSWER":    return "Short answer";
    default:                return type || "Question";
  }
}

// ── Build per-question display results from the backend's gradedResults ──────
// The backend is the source of truth for correctness and the answer key. When
// the student can still retake the test, the backend omits correctOptionId /
// correctOptionIds from `backendGraded` (see redactGradedResults on the
// server) so the answer key never reaches the client to be re-derived here —
// showing which questions were wrong is still fine, showing the right answer
// is not, since that would turn a retake into memorization.
function buildGradedResults(backendGraded, submission, test, redacted) {
  if (!Array.isArray(backendGraded) || !test?.questions) return [];
  const questions = Array.isArray(test.questions) ? test.questions : [];
  const answers   = Array.isArray(submission?.answers) ? submission.answers : [];
  const gradedById = new Map(backendGraded.map((g) => [String(g.questionId), g]));

  return questions.map((q) => {
    const graded  = gradedById.get(String(q.id)) || {};
    const answer  = answers.find((a) => String(a.questionId) === String(q.id));
    const options = Array.isArray(q.options) ? q.options : [];
    const isCorrect = graded.correct === true;

    if (q.type === "MULTIPLE_SELECT") {
      const selectedIds = (graded.selectedOptionIds || answer?.selectedOptionIds || []).map(String);
      const selectedTexts = options.filter((o) => selectedIds.includes(String(o.id))).map((o) => o.text);

      if (redacted) {
        return {
          questionId: q.id, questionText: q.questionText, type: q.type,
          correct: isCorrect, status: isCorrect ? "correct" : "wrong",
          selectedTexts, redacted: true,
        };
      }

      const correctIds   = (graded.correctOptionIds || []).map(String);
      const correctPicks = options.filter((o) => selectedIds.includes(String(o.id)) && correctIds.includes(String(o.id))).map((o) => o.text);
      const wrongPicks   = options.filter((o) => selectedIds.includes(String(o.id)) && !correctIds.includes(String(o.id))).map((o) => o.text);
      const missed       = options.filter((o) => correctIds.includes(String(o.id)) && !selectedIds.includes(String(o.id))).map((o) => o.text);
      const hasPartial   = correctPicks.length > 0 && (wrongPicks.length > 0 || missed.length > 0);
      const status       = isCorrect ? "correct" : hasPartial ? "partial" : "wrong";

      return {
        questionId: q.id, questionText: q.questionText, type: q.type,
        correct: isCorrect, status,
        correctPicks, wrongPicks, missed,
        selectedTexts: correctPicks.concat(wrongPicks),
        correctTexts: options.filter((o) => correctIds.includes(String(o.id))).map((o) => o.text),
      };
    }

    if (q.type === "MATCHING") {
      const matchCount = graded.matchCount ?? 0;
      const totalPairs = graded.totalPairs ?? (Array.isArray(q.pairs) ? q.pairs.length : 0);
      const status     = isCorrect ? "correct" : matchCount > 0 ? "partial" : "wrong";

      if (redacted) {
        return {
          questionId: q.id, questionText: q.questionText, type: q.type,
          correct: isCorrect, status, matchCount, totalPairs, redacted: true,
        };
      }

      // Student answer uses key "matchings" (from TestTakePage buildAnswerList),
      // not "pairs". The question uses "pairs" for its definition.
      const matchings     = answer?.matchings || [];
      const questionPairs = Array.isArray(q.pairs) ? q.pairs : [];
      // Student answer ids are opaque answer-choice ids, so correctness comes from the
      // server's matchedPromptIds, not from comparing ids.
      const matched = new Set((graded.matchedPromptIds || []).map(String));
      const pairsDisplay = questionPairs.map((p) => {
        const studentMatch = matchings.find((m) => String(m.promptId) === String(p.id));
        const matchCorrect = studentMatch != null && matched.has(String(p.id));
        const pickedPair   = matchCorrect ? p : null;
        return {
          id: p.id, prompt: p.prompt, answer: p.answer, matchCorrect,
          studentAnswer: pickedPair?.answer ?? null,
        };
      });

      return {
        questionId: q.id, questionText: q.questionText, type: q.type,
        correct: isCorrect, status, matchCount, totalPairs,
        pairs: pairsDisplay,
      };
    }

    if (q.type === "SHORT_ANSWER") {
      const textAnswer = graded.textAnswer || answer?.shortAnswer || "(no answer)";
      const accepted = Array.isArray(graded.acceptedAnswers) ? graded.acceptedAnswers : [];
      const referenceAnswer = redacted || accepted.length === 0 ? null : accepted.join(" / ");
      return {
        questionId: q.id, questionText: q.questionText, type: q.type,
        correct: isCorrect, status: isCorrect ? "correct" : "wrong",
        textAnswer, referenceAnswer,
        feedback: q.feedback || null,
      };
    }

    // MULTIPLE_CHOICE + TRUE_FALSE
    const selectedId = graded.selectedOptionId ?? answer?.selectedOptionId;
    const selected    = options.find((o) => String(o.id) === String(selectedId));
    const correctText = redacted ? null : options.find((o) => String(o.id) === String(graded.correctOptionId))?.text;
    return {
      questionId: q.id, questionText: q.questionText, type: q.type,
      correct: isCorrect, status: isCorrect ? "correct" : "wrong",
      selectedText: selected?.text || "(no answer)",
      correctText: correctText || (redacted ? null : "—"),
      redacted,
    };
  });
}

// ── Custom button ─────────────────────────────────────────────────────────────
function Btn({ variant = "outline", onClick, disabled, children, sx = {} }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: "7px",
    height: 36, px: "14px", borderRadius: "9px",
    border: "1px solid transparent",
    fontSize: 13.5, fontWeight: 600, fontFamily: "inherit",
    cursor: disabled ? "default" : "pointer",
    transition: "all .15s", textDecoration: "none",
    opacity: disabled ? 0.55 : 1,
  };
  const styles = {
    primary: { bgcolor: T.primary, color: "#fff", "&:hover": { bgcolor: T.p700 } },
    outline: { bgcolor: T.surface, color: T.ink, borderColor: T.line, "&:hover": { bgcolor: T.surface2, borderColor: "var(--color-hairline-cool)" } },
    ghost:   { bgcolor: "transparent", color: T.ink2, "&:hover": { bgcolor: T.surface3, color: T.ink } },
  };
  return (
    <Box component="button" onClick={disabled ? undefined : onClick}
      sx={{ ...base, ...styles[variant], ...sx }}>
      {children}
    </Box>
  );
}

// ── Score ring (conic gradient) ────────────────────────────────────────────────
function ScoreRing({ pct, passed }) {
  const ringColor = passed ? T.green : T.danger;
  const trackColor = T.line2;
  return (
    <Box sx={{
      width: 96, height: 96, borderRadius: "50%", flexShrink: 0,
      background: `conic-gradient(${ringColor} ${pct}%, ${trackColor} 0%)`,
      display: "grid", placeItems: "center", position: "relative",
    }}>
      <Box sx={{
        position: "absolute", inset: "9px",
        background: T.surface, borderRadius: "50%",
      }} />
      <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", lineHeight: 1 }}>
        <Box sx={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", color: T.ink, lineHeight: 1, fontFamily: "var(--font-display)" }}>
          {pct}<Box component="span" sx={{ fontSize: 14, color: T.ink3, fontWeight: 600 }}>%</Box>
        </Box>
        <Box sx={{ fontSize: 10, fontWeight: 600, letterSpacing: ".12em", textTransform: "uppercase", color: T.ink3, mt: "4px" }}>
          your score
        </Box>
      </Box>
    </Box>
  );
}

// ── Status icon (28px circle with icon) ───────────────────────────────────────
function StatusIcon({ status }) {
  const cfg = {
    correct: { bg: T.green,  Icon: CheckIcon },
    partial: { bg: T.amber,  Icon: WarningAmberIcon },
    wrong:   { bg: T.danger, Icon: CloseIcon },
  }[status] || { bg: T.ink3, Icon: CheckIcon };
  return (
    <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: cfg.bg, display: "grid", placeItems: "center", flexShrink: 0, mt: "2px" }}>
      <cfg.Icon sx={{ fontSize: 15, color: "#fff" }} />
    </Box>
  );
}

// ── Option pill ────────────────────────────────────────────────────────────────
function Pill({ kind, children }) {
  const styles = {
    correct: { bgcolor: T.green50, color: T.green, border: `1px solid ${T.green100}`, fontWeight: 600 },
    wrong:   { bgcolor: T.danger50, color: T.danger, border: `1px solid ${T.danger100}`, fontWeight: 600, textDecoration: "line-through" },
    missed:  { bgcolor: T.surface, color: T.green, border: `1px dashed ${T.green}`, fontWeight: 600 },
    neutral: { bgcolor: T.surface3, color: T.ink2, border: "1px solid transparent", fontWeight: 500 },
  }[kind] || {};
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      px: "10px", py: "4px", borderRadius: "999px",
      fontSize: 12.5, ...styles,
    }}>
      {children}
    </Box>
  );
}

// ── Answer row ─────────────────────────────────────────────────────────────────
function AnsRow({ label, children, first }) {
  return (
    <Box sx={{
      display: "grid", gridTemplateColumns: "104px 1fr", gap: "12px",
      py: "8px", alignItems: "start", fontSize: 13.5,
      borderTop: first ? "none" : `1px dashed ${T.line2}`,
    }}>
      <Box sx={{ color: T.ink3, fontSize: 11.5, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", pt: "3px" }}>
        {label}
      </Box>
      <Box>{children}</Box>
    </Box>
  );
}

// ── Feedback callout ───────────────────────────────────────────────────────────
function Feedback({ prefix, children, green }) {
  return (
    <Box sx={{
      mt: "10px", p: "10px 12px",
      bgcolor: green ? T.green50 : T.p50,
      borderLeft: `3px solid ${green ? T.green : T.primary}`,
      borderRadius: "6px", fontSize: 12.5,
      color: T.ink2, lineHeight: 1.5,
    }}>
      <Box component="b" sx={{ color: green ? T.green : T.primary, fontWeight: 700 }}>{prefix}</Box>
      {" "}{children}
    </Box>
  );
}

// ── Question card ──────────────────────────────────────────────────────────────
function QuestionCard({ result, index }) {
  const { status } = result;
  const [open, setOpen] = React.useState(false);
  const borderColor = status === "correct" ? T.green : status === "partial" ? T.amber : T.danger;
  const ptColor     = status === "correct" ? T.green : status === "partial" ? T.amber : T.danger;
  const isCorrect   = status === "correct";

  return (
    <Box data-question-card="" sx={{
      bgcolor: T.surface, border: `1px solid ${borderColor}`,
      borderRadius: "12px", mb: "12px", overflow: "hidden",
      transition: "box-shadow .12s",
      "&:hover": { boxShadow: T.shadowSm },
    }}>
      {/* Header row */}
      <Box
        component="button"
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: "14px", alignItems: "flex-start",
          p: "16px 20px", width: "100%", textAlign: "left", fontFamily: "inherit", border: "none",
          bgcolor: "transparent", cursor: "pointer",
        }}
      >
        <StatusIcon status={status} />
        <Box>
          <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink3, fontFamily: T.mono, mb: "3px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            Q{index + 1}
            <Box component="span" sx={{ bgcolor: T.surface3, color: T.ink2, px: "7px", py: "2px", borderRadius: "4px", letterSpacing: ".04em", fontSize: 10 }}>
              {getTypeLabel(result.type)}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 15.5, fontWeight: 600, color: T.ink, lineHeight: 1.35, letterSpacing: "-0.005em", m: 0 }}>
            {result.questionText}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right", fontFamily: T.mono, fontSize: 12.5, color: T.ink3, whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ fontSize: 16, fontWeight: 700, color: ptColor, fontFamily: "Inter, sans-serif" }}>
            {isCorrect ? "✓" : status === "partial" ? "~" : "✗"}
          </Box>
        </Box>
        <Box sx={{ color: T.ink3, display: "grid", placeItems: "center", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", mt: "2px" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </Box>
      </Box>

      {/* Body */}
      {open && (
      <Box sx={{ px: "20px", pb: "16px", pl: { xs: "20px", sm: "62px" } }}>
        {/* MULTIPLE_CHOICE / TRUE_FALSE */}
        {(result.type === "MULTIPLE_CHOICE" || result.type === "TRUE_FALSE") && (
          <>
            <AnsRow label="Your answer" first>
              <Box component="span" sx={{ color: isCorrect ? T.green : T.danger, fontWeight: 600 }}>
                {result.selectedText}
              </Box>
            </AnsRow>
            {!isCorrect && !result.redacted && (
              <AnsRow label="Correct">
                <Box component="span" sx={{ color: T.green, fontWeight: 600 }}>
                  {result.correctText}
                </Box>
              </AnsRow>
            )}
            {!isCorrect && result.redacted && (
              <Box sx={{ mt: "6px", fontSize: 12.5, color: T.ink3, fontStyle: "italic" }}>
                Correct answer hidden — you can still retake this test.
              </Box>
            )}
            {result.feedback && (
              <Feedback prefix="Review:">{result.feedback}</Feedback>
            )}
          </>
        )}

        {/* MULTIPLE_SELECT */}
        {result.type === "MULTIPLE_SELECT" && (
          <>
            <AnsRow label="You picked" first>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {result.redacted
                  ? (result.selectedTexts?.length > 0
                      ? result.selectedTexts.map((t, i) => <Pill key={i} kind="neutral">{t}</Pill>)
                      : <Box sx={{ color: T.ink3, fontSize: 13, fontStyle: "italic" }}>(no selection)</Box>)
                  : <>
                      {result.correctPicks?.map((t, i) => <Pill key={i} kind="correct">{t}</Pill>)}
                      {result.wrongPicks?.map((t, i) => <Pill key={i} kind="wrong">{t}</Pill>)}
                      {result.selectedTexts?.length === 0 && <Box sx={{ color: T.ink3, fontSize: 13, fontStyle: "italic" }}>(no selection)</Box>}
                    </>
                }
              </Box>
            </AnsRow>
            {!result.redacted && result.missed?.length > 0 && (
              <AnsRow label="Missed">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {result.missed.map((t, i) => <Pill key={i} kind="missed">{t}</Pill>)}
                </Box>
              </AnsRow>
            )}
            {result.redacted && !result.correct && (
              <Box sx={{ mt: "6px", fontSize: 12.5, color: T.ink3, fontStyle: "italic" }}>
                Correct answer hidden — you can still retake this test.
              </Box>
            )}
            {result.feedback && (
              <Feedback prefix="Partial credit:">{result.feedback}</Feedback>
            )}
          </>
        )}

        {/* MATCHING */}
        {result.type === "MATCHING" && (
          <>
            <AnsRow label="Pairs" first>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 16px 1fr", gap: "6px 10px", mt: "4px" }}>
                {result.redacted || (result.pairs || []).length === 0
                  ? (
                    <Box sx={{ gridColumn: "1/-1", color: T.ink3, fontSize: 13 }}>
                      {result.matchCount}/{result.totalPairs} pairs matched correctly
                      {result.redacted && !result.correct && (
                        <Box component="span" sx={{ fontStyle: "italic" }}> — details hidden, you can still retake this test.</Box>
                      )}
                    </Box>
                  )
                  : (result.pairs || []).map((p, i) => {
                      const pairColor = p.matchCorrect ? T.green : T.danger;
                      const pairBg    = p.matchCorrect ? T.green50 : T.danger50;
                      // Right cell shows correct answer (green) or what student picked (red)
                      const rightText = p.matchCorrect
                        ? p.answer
                        : (p.studentAnswer != null ? p.studentAnswer : "(unmatched)");
                      return (
                        <React.Fragment key={i}>
                          <Box sx={{ bgcolor: pairBg, color: pairColor, fontWeight: 600, fontSize: 12.5, p: "6px 10px", borderRadius: "6px" }}>{p.prompt}</Box>
                          <Box sx={{ display: "grid", placeItems: "center", color: pairColor, fontFamily: T.mono }}>→</Box>
                          <Box sx={{ bgcolor: pairBg, color: pairColor, fontWeight: 600, fontSize: 12.5, p: "6px 10px", borderRadius: "6px" }}>
                            {rightText}
                            {!p.matchCorrect && p.studentAnswer !== p.answer && (
                              <Box component="span" sx={{ display: "block", fontSize: 11, color: T.green, fontWeight: 400, mt: "2px" }}>
                                ✓ {p.answer}
                              </Box>
                            )}
                          </Box>
                        </React.Fragment>
                      );
                    })
                }
              </Box>
            </AnsRow>
          </>
        )}

        {/* SHORT_ANSWER */}
        {result.type === "SHORT_ANSWER" && (
          <>
            <AnsRow label="Your answer" first>
              <Box sx={{ bgcolor: T.surface3, p: "10px 12px", borderRadius: "8px", fontStyle: "italic", color: T.ink2, fontWeight: 400 }}>
                "{result.textAnswer}"
              </Box>
            </AnsRow>
            {result.referenceAnswer && (
              <AnsRow label="Reference">
                <Box sx={{ color: T.ink2, fontWeight: 500 }}>{result.referenceAnswer}</Box>
              </AnsRow>
            )}
            {result.feedback && (
              <Feedback prefix="Grader note:">{result.feedback}</Feedback>
            )}
          </>
        )}
      </Box>
      )}
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function TestResultsPage() {
  const { classId, sessionId, testId } = useParams();
  const navigate = useNavigate();
  const { user } = useTenant();

  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [testData,      setTestData]      = useState(null);
  const [submission,    setSubmission]    = useState(null);
  const [gradedResults, setGradedResults] = useState([]);
  const [canRetake,     setCanRetake]     = useState(false);
  const [submitCount,   setSubmitCount]   = useState(0);
  const [, setRedacted]      = useState(false);
  const [bestPassed,    setBestPassed]    = useState(false);
  const [effectiveMax,  setEffectiveMax]  = useState(null);

  const studentId = user?.id;

  useEffect(() => {
    if (!studentId) { setError("Please log in to view your results."); setLoading(false); return; }
    setLoading(true);
    setError(null);
    fetchLatestTestSubmission(testId, studentId)
      .then((data) => {
        if (!data?.submission) { setError("No results found. Take the test first."); return; }
        const sub = data.submission;
        const tst = data.test;
        const isRedacted = typeof data.redacted === "boolean" ? data.redacted : data.canRetake === true;
        setSubmission(sub);
        setTestData(tst);
        setCanRetake(data.canRetake === true);
        setSubmitCount(data.submissionCount ?? 1);
        setBestPassed(data.bestPassed === true);
        setEffectiveMax(typeof data.effectiveMax === "number" ? data.effectiveMax : null);
        setRedacted(isRedacted);
        setGradedResults(buildGradedResults(data.gradedResults, sub, tst, isRedacted));
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || "Failed to load results."))
      .finally(() => setLoading(false));
  }, [testId, studentId]);

  const backPath    = sessionId ? `/classes/${classId}/session/${sessionId}` : classId ? `/classes/${classId}` : "/classes";
  const retakePath  = sessionId ? `/classes/${classId}/session/${sessionId}/test/${testId}` : `/classes/${classId}/tests/${testId}`;

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress size={36} />
    </Box>
  );

  if (error || !submission) return (
    <Box sx={{ maxWidth: 640, mx: "auto", p: 3 }}>
      <Box sx={{ bgcolor: T.danger50, color: T.danger, p: "12px 16px", borderRadius: "10px", mb: 2, fontSize: 14, fontWeight: 500 }}>
        {error || "No results available."}
      </Box>
      <Btn variant="outline" onClick={() => navigate(backPath)}>
        <ArrowBackIcon sx={{ fontSize: 15 }} /> Back
      </Btn>
    </Box>
  );

  const score       = submission.score ?? 0;
  const totalPoints = submission.totalPoints ?? gradedResults.length;
  const scorePct    = totalPoints > 0 ? Math.round((score / totalPoints) * 100) : 0;
  const passingScore = testData?.passingScore;
  // passingScore is stored as a PERCENTAGE (0-100), not a raw point count.
  // A passingScore of 0 is treated as "not configured" (falls back to 70%) —
  // matching the backend's determinePassed(); a literal 0% threshold would be
  // trivially met by a score of 0.
  const passingPct   = passingScore > 0 ? passingScore : 70;
  // Recalculate passed from score — backend may have stored a stale/incorrect value.
  const latestPassed = totalPoints > 0 ? (scorePct >= passingPct) : (submission.passed === true);
  // Messaging follows the best attempt (same rule as sign-off): one pass counts even if a later retake failed.
  const passed       = latestPassed || bestPassed;
  const gap          = passingPct - scorePct;
  const correctCount = gradedResults.filter((r) => r.status === "correct").length;
  const wrongCount   = gradedResults.filter((r) => r.status === "wrong").length;
  const partialCount = gradedResults.filter((r) => r.status === "partial").length;
  // maxRetakes is total attempts allowed; minimum 1 to avoid "X of 0" display.
  // effectiveMax from the server includes granted retakes; fall back to the test's base.
  const maxRetakes   = Math.max(effectiveMax ?? testData?.maxRetakes ?? 1, 1);

  // Time taken
  const timeTakenSec = submission.startedAt && submission.submittedAt
    ? Math.round((new Date(submission.submittedAt) - new Date(submission.startedAt)) / 1000)
    : null;
  const timeLimitSec = testData?.timeLimitMinutes ? testData.timeLimitMinutes * 60 : null;

  const scopeLabel  = testData?.classLevel === false ? "Session test" : "Written test · Class-level";
  const heroHeadline = passed
    ? "You passed — well done!"
    : gap > 0 && gap <= 10
    ? `Close — you missed the passing mark by ${gap}%`
    : gap > 10
    ? `You scored ${scorePct}%; the passing mark is ${passingPct}%. Review the answers below and try again.`
    : "You need to meet the passing threshold to pass.";
  const heroSub = passed && !latestPassed
    ? `This attempt scored ${score} of ${totalPoints} (${scorePct}%), but you already passed on an earlier attempt (passing: ${passingPct}%).`
    : passed
    ? `You scored ${score} of ${totalPoints} — ${scorePct}% (passing: ${passingPct}%).`
    : `You scored ${score} of ${totalPoints} points. Passing is ${passingPct}%. ${
        canRetake ? "You have an additional attempt available." :
        submitCount >= maxRetakes ? "You've used all available attempts." : ""
      }`;

  return (
    <>
    <GlobalStyles styles={{
      "@media print": {
        "@page": { margin: "0.65in 0.75in" },
        "body": { background: "white !important" },
        "*": {
          WebkitPrintColorAdjust: "exact !important",
          printColorAdjust: "exact !important",
          colorAdjust: "exact !important",
          transition: "none !important",
          animation: "none !important",
        },
        "[data-question-card]": {
          pageBreakInside: "avoid",
          breakInside: "avoid",
          boxShadow: "none !important",
          marginBottom: "14px",
        },
        "[data-hero-card]": {
          boxShadow: "none !important",
          pageBreakInside: "avoid",
          breakInside: "avoid",
        },
        "[data-no-print]": { display: "none !important" },
        "[data-print-only]": { display: "block !important" },
      },
    }} />
    <Box sx={{ bgcolor: T.pageBg, minHeight: "100vh", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", WebkitFontSmoothing: "antialiased" }}>
      <Box sx={{ maxWidth: 1080, mx: "auto", p: { xs: "18px 18px 60px", sm: "28px 40px 80px" } }}>

        {/* Print-only report header (hidden on screen) */}
        <Box data-print-only="" sx={{
          display: "none",
          mb: "20px", pb: "16px",
          borderBottom: `2px solid ${T.primary}`,
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Box sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: T.ink3, mb: "4px" }}>
                Test Results Report
              </Box>
              <Box sx={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", color: T.primary, lineHeight: 1.1, fontFamily: "var(--font-display)" }}>
                {testData?.title || "Test Results"}
              </Box>
            </Box>
            <Box sx={{ textAlign: "right", fontSize: 12, color: T.ink3, fontFamily: T.mono, mt: "4px", lineHeight: 1.8 }}>
              <Box>Submitted: <Box component="b" sx={{ color: T.ink2 }}>{formatDate(submission.submittedAt)}</Box></Box>
              <Box>Attempt: <Box component="b" sx={{ color: T.ink2 }}>{submitCount} of {maxRetakes}</Box></Box>
            </Box>
          </Box>
          <Box sx={{ mt: "10px", display: "flex", gap: "20px", fontSize: 13 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: passed ? T.green : T.danger }} />
              <Box component="b" sx={{ color: passed ? T.green : T.danger, fontSize: 14, fontWeight: 700 }}>
                {passed ? "Passed" : "Not Passed"}
              </Box>
              <Box sx={{ color: T.ink3 }}>— {scorePct}% ({score}/{totalPoints} pts)</Box>
            </Box>
            <Box sx={{ color: T.ink3 }}>
              Passing mark: <Box component="b" sx={{ color: T.ink2 }}>{passingPct}%</Box>
            </Box>
          </Box>
        </Box>

        {/* Page header */}
        <Box sx={{ mb: "22px" }}>
          <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: T.ink3, mb: "4px" }}>
            {scopeLabel}
          </Box>
          <Typography sx={{ fontSize: { xs: 24, sm: 32 }, fontWeight: 800, letterSpacing: "-0.02em", color: T.primary, m: 0, lineHeight: 1.1, fontFamily: "var(--font-display)" }}>
            {testData?.title || "Test Results"}
          </Typography>
          <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", mt: "6px", fontSize: 13, color: T.ink3, fontFamily: T.mono }}>
            <Box>Submitted <Box component="b" sx={{ color: T.ink2, fontWeight: 600 }}>{formatDate(submission.submittedAt)}</Box></Box>
            <Box>Attempt <Box component="b" sx={{ color: T.ink2, fontWeight: 600 }}>{submitCount} of {maxRetakes}</Box></Box>
          </Box>
        </Box>

        {/* Hero card */}
        <Box data-hero-card="" sx={{
          bgcolor: T.surface, borderRadius: "16px", boxShadow: T.shadow,
          overflow: "hidden", mb: "20px",
          border: `1px solid ${T.line2}`,
          borderLeft: `4px solid ${passed ? T.green : T.danger}`,
        }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "auto 1fr", sm: "auto 1fr auto" }, gap: "24px", alignItems: "center", p: "24px 28px" }}>
            <ScoreRing pct={scorePct} passed={passed} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                px: "10px", py: "4px", borderRadius: "999px",
                bgcolor: passed ? T.green50 : T.danger50,
                color: passed ? T.green : T.danger,
                fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
                mb: "8px",
              }}>
                {passed
                  ? <CheckIcon sx={{ fontSize: 14 }} />
                  : <CloseIcon sx={{ fontSize: 14 }} />}
                {passed ? "Passed" : "Not Passed"}
              </Box>
              <Typography sx={{ fontSize: { xs: 18, sm: 24 }, fontWeight: 700, letterSpacing: "-0.01em", color: T.ink, m: 0, mb: "4px", fontFamily: "var(--font-display)" }}>
                {heroHeadline}
              </Typography>
              <Typography sx={{ color: T.ink3, fontSize: 14, m: 0 }}>
                {heroSub.split(/(<b>.*?<\/b>)/g).map((part, i) =>
                  part.startsWith("<b>") ? <Box component="b" key={i} sx={{ color: T.ink2, fontWeight: 600 }}>{part.replace(/<\/?b>/g, "")}</Box> : part
                )}
              </Typography>
            </Box>
            <Box data-no-print="" sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, gap: "8px", gridColumn: { xs: "1 / -1", sm: "auto" }, flexWrap: "wrap" }}>
              {canRetake && retakePath && (
                <Btn variant="primary" onClick={() => navigate(retakePath)}>
                  <ReplayIcon sx={{ fontSize: 14 }} /> Retake Test
                </Btn>
              )}
              <Btn variant="outline" onClick={() => window.print()}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} /> Download PDF
              </Btn>
            </Box>
          </Box>

          {/* Stats strip */}
          <Box sx={{
            display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
            borderTop: `1px solid ${T.line2}`, bgcolor: T.surface2,
          }}>
            {[
              { label: "Total points", dot: T.ink3, value: `${score} / ${totalPoints}`, delta: `${gradedResults.length} question${gradedResults.length !== 1 ? "s" : ""}`, deltaColor: null },
              { label: "Correct",      dot: T.green, value: `${correctCount} of ${gradedResults.length}`, delta: `${scorePct}% accuracy`, deltaColor: T.green },
              { label: "Incorrect",    dot: T.danger, value: `${wrongCount + partialCount} of ${gradedResults.length}`, delta: `${wrongCount + partialCount} to review`, deltaColor: wrongCount + partialCount > 0 ? T.danger : null },
              { label: "Time taken",   dot: T.blue,  value: timeTakenSec != null ? formatTimeSec(timeTakenSec) : "—", delta: timeLimitSec ? `limit: ${formatTimeSec(timeLimitSec)}` : "no limit", deltaColor: null },
            ].map((cell, i) => (
              <Box key={i} sx={{ p: "14px 20px", borderRight: { xs: i % 2 === 1 ? "none" : `1px solid ${T.line2}`, sm: i === 3 ? "none" : `1px solid ${T.line2}` }, borderBottom: { xs: i < 2 ? `1px solid ${T.line2}` : "none", sm: "none" } }}>
                <Box sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink3, display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: cell.dot, flexShrink: 0 }} />
                  {cell.label}
                </Box>
                <Box sx={{ mt: "4px", fontSize: 18, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em", fontFamily: "var(--font-display)" }}>
                  {cell.value}
                </Box>
                <Box sx={{ fontSize: 11.5, color: cell.deltaColor ?? T.ink3, mt: "1px", fontWeight: 500 }}>
                  {cell.delta}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Answers section */}
        {gradedResults.length > 0 && (
          <>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "14px", mb: "14px", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.ink, letterSpacing: "-0.005em", m: 0, fontFamily: "var(--font-display)" }}>
                Your answers
              </Typography>
              <Box sx={{ fontSize: 12.5, color: T.ink3, fontFamily: T.mono }}>
                {gradedResults.length} question{gradedResults.length !== 1 ? "s" : ""} · {totalPoints} point{totalPoints !== 1 ? "s" : ""} possible
              </Box>
            </Box>

            <Box data-no-print="" sx={{ display: "flex", gap: "10px", alignItems: "center", mb: "16px", flexWrap: "wrap" }}>
              {[
                { dot: T.green, label: "Correct" },
                { dot: T.amber, label: "Partial credit" },
                { dot: T.danger, label: "Incorrect" },
              ].map((item) => (
                <Box key={item.label} sx={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  px: "9px", py: "3px", borderRadius: "999px",
                  bgcolor: T.surface, border: `1px solid ${T.line2}`,
                  fontSize: 11.5, fontWeight: 600, color: T.ink3,
                }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: item.dot }} />
                  {item.label}
                </Box>
              ))}
            </Box>

            {gradedResults.map((r, i) => (
              <QuestionCard key={r.questionId ?? i} result={r} index={i} />
            ))}
          </>
        )}

        {/* Footer — hidden in print */}
        <Box data-no-print="" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "24px", pt: "20px", borderTop: `1px solid ${T.line2}`, color: T.ink3, fontSize: 12.5, flexWrap: "wrap", gap: "12px" }}>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Btn variant="outline" onClick={() => navigate(backPath)}>
              <ArrowBackIcon sx={{ fontSize: 14 }} /> Back to Session
            </Btn>
          </Box>
          <Box sx={{ fontFamily: T.mono }}>
            {submission.submittedAt ? `Submitted at ${new Date(submission.submittedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : ""}
            {" · "}graded automatically
          </Box>
        </Box>

        {/* Print-only footer */}
        <Box data-print-only="" sx={{
          mt: "32px", pt: "12px", borderTop: `1px solid ${T.line}`,
          fontSize: 11, color: T.ink3, fontFamily: T.mono,
          justifyContent: "space-between",
        }}>
          <span>Training Center · Test Results Report</span>
          <span>Printed {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
        </Box>
      </Box>
    </Box>
    </>
  );
}
